import { Link } from "react-router-dom";
import { Calendar, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "react-helmet";

const blogPosts = [
  {
    slug: "how-to-build-successful-bouldering-gym",
    title: "How to Build a Successful Bouldering Gym: Complete Guide 2024",
    excerpt: "Essential steps for planning, designing, and constructing a profitable bouldering gym. Learn from industry experts with over a decade of experience.",
    category: "Construction",
    date: "2024-01-15",
    readTime: "8 min read",
    image: "bouldering-construction"
  },
  {
    slug: "climbing-holds-guide-types-materials",
    title: "Climbing Holds Guide: Types, Materials & Selection for Your Gym",
    excerpt: "Everything you need to know about climbing holds - from materials and shapes to durability and route setting. Expert insights from hold manufacturers.",
    category: "Equipment",
    date: "2024-01-10",
    readTime: "6 min read",
    image: "climbing-holds"
  },
  {
    slug: "climbing-gym-management-software-2024",
    title: "Best Climbing Gym Management Software Solutions in 2024",
    excerpt: "Comprehensive guide to gym management software, booking systems, and automation tools. Streamline operations and improve member experience.",
    category: "Technology",
    date: "2024-01-05",
    readTime: "7 min read",
    image: "gym-software"
  }
];

const Blog = () => {
  return (
    <>
      <Helmet>
        <title>Climbing Gym Blog - Expert Insights & Industry News | Falkors Group</title>
        <meta name="description" content="Expert articles about bouldering gym construction, climbing holds, management software, and industry trends. Learn from professionals with 10+ years experience." />
        <meta name="keywords" content="climbing gym blog, bouldering gym construction, climbing holds, gym management software, climbing industry" />
        <link rel="canonical" href="https://falkors.com/blog" />
        
        <meta property="og:title" content="Climbing Gym Blog - Expert Insights & Industry News" />
        <meta property="og:description" content="Expert articles about bouldering gym construction, climbing holds, and gym management." />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Falkors Climbing Gym Blog",
            "description": "Expert insights on climbing gym construction and management",
            "publisher": {
              "@type": "Organization",
              "name": "Falkors Group",
              "url": "https://falkors.com"
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-32 pb-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="max-w-3xl mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary">Blog & Insights</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display text-foreground mb-6">
                EXPERT INSIGHTS
              </h1>
              <p className="text-xl text-muted-foreground">
                Industry knowledge, construction guides, and climbing gym management tips 
                from professionals with over a decade of experience.
              </p>
            </div>
            
            {/* Blog Posts Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <article key={index}>
                  <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full flex flex-col">
                    {/* Image Placeholder */}
                    <div className="h-48 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                      <span className="text-4xl">🧗</span>
                    </div>
                    
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-3 mb-4">
                        <Badge variant="outline">{post.category}</Badge>
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                      </div>
                      
                      <h2 className="text-xl font-display text-foreground mb-3 leading-tight">
                        {post.title}
                      </h2>
                      
                      <p className="text-muted-foreground mb-4 flex-1">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          {post.readTime}
                        </span>
                        <Link to={`/blog/${post.slug}`}>
                          <Button variant="ghost" className="group">
                            Read More
                            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </Card>
                </article>
              ))}
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Blog;